import { ArchiveSearchResult, PatientOverviewItem, StatusEnum } from '@vetahealth/tuna-can-api'
import { parsePhoneNumber } from 'libphonenumber-js'
import { useState } from 'react'

import { usePatientsStore } from '../../stores/patients'
import { API } from '../api'
import { TrackingEvent, trackEvent } from '../tracking'
import { useDebounce } from './useDebounce'

interface UsePatientSelectionProps {
  search: string
}

export type PatientSearchResult = PatientOverviewItem | (ArchiveSearchResult & { status: StatusEnum })

const searchProperties: Array<keyof PatientOverviewItem> = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'clientIdentifier',
  'mobilePhone',
  'landlinePhone',
  'secondaryMobilePhone',
  'secondaryLandlinePhone',
]

function matchPattern(patient: Partial<PatientOverviewItem>, searchTerm: string): boolean {
  // first name prefix match
  if (patient.firstName?.startsWith(searchTerm)) return true
  // last name prefix match
  if (patient.lastName?.startsWith(searchTerm)) return true
  // birth year full match
  if (patient.dateOfBirth?.split('-')[0].toLowerCase() === searchTerm) return true
  // MRN full match
  if (patient.clientIdentifier === searchTerm) return true

  // Helper function to check if search term appears as consecutive digits in phone number
  const matchesPhone = (phone: string | undefined): boolean => {
    if (!phone) return false
    // Don't do phone matching if search term contains letters (not a phone number search)
    if (/[a-z]/.test(searchTerm)) return false
    // Extract only digits from search term
    const searchDigits = searchTerm.replace(/\D/g, '')
    // Only perform phone matching if search term contains digits
    if (!searchDigits) return false
    // Extract only digits from phone
    const phoneDigits = phone.replace(/\D/g, '')
    // Check if search digits appear consecutively anywhere in phone digits
    return phoneDigits.includes(searchDigits)
  }

  // Check all phone fields for consecutive digit matches
  if (matchesPhone(patient.mobilePhone)) return true
  if (matchesPhone(patient.landlinePhone)) return true
  if (matchesPhone(patient.secondaryMobilePhone)) return true
  if (matchesPhone(patient.secondaryLandlinePhone)) return true

  return false
}

export function prepareQuery(searchValue: string): string[] {
  const resultQueries: string[] = []
  const parts = searchValue.toLowerCase().split(/\s/)
  let currentTerm = ''
  for (const part of parts) {
    if (!part.trim()) continue // ignore empty (whitespace only) parts
    const isAlphaNum = part.match(/[A-Za-z]/)
    if (isAlphaNum) {
      // not a number, finish previous merge and push unchanged
      if (currentTerm) {
        resultQueries.push(currentTerm)
        currentTerm = ''
      }
      resultQueries.push(part.trim())
      continue
    }
    // merge with consecutive numbers (if any)
    currentTerm += part.trim()
  }
  if (currentTerm) resultQueries.push(currentTerm)
  return resultQueries.map((query) => {
    if (/[A-Za-z]/.test(query)) return query // alphanumeric => not a phone number, don't parse
    // Don't parse short digit-only sequences (< 7 digits) as they're likely for substring matching
    const digitsOnly = query.replace(/\D/g, '')
    if (digitsOnly.length > 0 && digitsOnly.length < 7 && query === digitsOnly) {
      return digitsOnly // Return digits as-is for short numbers
    }
    try {
      const phone = parsePhoneNumber(query, { defaultCountry: 'US' })
      // if we cannot detect a valid phone, we remove all non-digits except a leading +, so full international number
      // search will still work, even if the number is detected as invalid
      return phone.isValid() ? phone.format('E.164') : query.replace(/[^0-9+]|(?<!^)\+/g, '')
    } catch (_e) {
      return query.replace(/[^0-9+]|(?<!^)\+/g, '')
    }
  })
}

export function searchPatients(patients: Array<PatientOverviewItem>, searchValue: string): Array<PatientOverviewItem> {
  const searchValues = prepareQuery(searchValue)

  return patients.filter((patient) => {
    const patientValuesLowercase: Partial<PatientOverviewItem> = Object.fromEntries(
      Object.entries(patient)
        .filter(([key, value]) => typeof value === 'string' && (searchProperties as Array<string>).includes(key))
        .map(([key, value]) => [key, value.toLowerCase()]),
    )
    return searchValues.every((splitSearchValue) => matchPattern(patientValuesLowercase, splitSearchValue))
  })
}

export function usePatientSearch({ search }: UsePatientSelectionProps): Array<PatientSearchResult> {
  const [patients, getPatients] = usePatientsStore((state) => [state.patients, state.getPatients])

  const [localResults, setLocalResults] = useState<PatientOverviewItem[]>([])
  const [archivedResults, setArchivedResults] = useState<Array<ArchiveSearchResult & { status: StatusEnum }>>([])

  void getPatients()

  useDebounce(
    () => {
      if (search) trackEvent(TrackingEvent.patientSearchEntered, { query: search })
    },
    500,
    [search],
  )

  useDebounce(
    () => {
      if (search.trim().length > 2) {
        const searchResult = searchPatients(patients, search)
        if (searchResult) setLocalResults(searchResult)
      } else {
        setLocalResults([])
      }
    },
    100,
    [search, patients],
  )

  useDebounce(
    () => {
      if (search.trim().length > 2) {
        ;(async () => {
          const searchResult = await API.quickSearchArchive(prepareQuery(search).join(' '))
          const archivedResults = searchResult
            ? searchResult.map((result) => ({ ...result, status: StatusEnum.Archived }))
            : []
          if (archivedResults) setArchivedResults(archivedResults)
        })()
      } else {
        setArchivedResults([])
      }
    },
    300,
    [search],
  )

  return [...localResults, ...archivedResults]
}
